/**
 * Route table mirroring §7.1.
 *
 *   Browse                        the marketplace; the only surface a guest sees
 *   My Bots    -> [bot]  ->        Console | Character | Accounts | Schedule |
 *                                  Library | Listing | Activity
 *   My Rentals -> [bot]  ->        Console | Library | Activity
 *   Account    ->                  Billing | Credits | Profile | Security | Notifications
 *
 * Character / Accounts / Schedule / Listing exist only under My Bots. When a
 * listing grants a renter `edit_character` or `edit_schedule` those controls
 * surface inside the Console, not as new nav — a renter must never see a nav
 * section that is mostly locked to them.
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { SessionProvider, useSession } from './session/SessionContext';
import { NotificationsProvider } from './hooks/useNotifications';
import { AppShell } from './components/AppShell';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { Loading } from './components/ui';

import { Browse } from './routes/Browse';
import { BotProfile } from './routes/BotProfile';
import { Login } from './routes/auth/Login';
import { Signup } from './routes/auth/Signup';
import { Verify } from './routes/auth/Verify';
import { TwoFactorChallenge } from './routes/auth/TwoFactorChallenge';

import { MyBots } from './routes/bots/MyBots';
import { BotWizard } from './routes/bots/BotWizard';
import { BotLayout } from './routes/bots/BotLayout';
import { CharacterEditor } from './routes/bots/CharacterEditor';
import { ConnectedAccounts } from './routes/bots/ConnectedAccounts';
import { ScheduleEditor } from './routes/bots/ScheduleEditor';
import { AssetLibrary } from './routes/bots/AssetLibrary';
import { ListingEditor } from './routes/bots/ListingEditor';
import { ActivityLog } from './routes/bots/ActivityLog';
import { DirectMessages } from './routes/console/DirectMessages';
import { OwnerConsoleRoute } from './routes/bots/OwnerConsoleRoute';

import { MyRentals } from './routes/rentals/MyRentals';
import { RentalLayout } from './routes/rentals/RentalLayout';
import { RenterConsoleRoute } from './routes/rentals/RenterConsoleRoute';
import { RentalRequest } from './routes/rentals/RentalRequest';
import { RentalApproval } from './routes/rentals/RentalApproval';
import { RentalConsent } from './routes/rentals/RentalConsent';
import { RentalReview } from './routes/rentals/RentalReview';

import { AccountLayout } from './routes/account/AccountLayout';
import { Billing } from './routes/account/Billing';
import { Credits } from './routes/account/Credits';
import { Profile } from './routes/account/Profile';
import { Security } from './routes/account/Security';
import { NotificationPreferencesRoute } from './routes/account/NotificationPreferences';
import { NotFound } from './routes/NotFound';

function RequireSession({ children }: { children: ReactNode }) {
  const { authenticated, loading } = useSession();
  const location = useLocation();
  if (loading) return <Loading label="Checking your session" />;
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  // Keyed on the path so navigating away from a broken screen clears the error
  // rather than stranding the user on it.
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <RouteTable />
    </RouteErrorBoundary>
  );
}

function RouteTable() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/browse" replace />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/listings/:listingId" element={<BotProfile />} />
      <Route
        path="/listings/:listingId/request"
        element={
          <RequireSession>
            <RentalRequest />
          </RequireSession>
        }
      />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/login/2fa" element={<TwoFactorChallenge />} />

      <Route
        path="/bots"
        element={
          <RequireSession>
            <MyBots />
          </RequireSession>
        }
      />
      <Route
        path="/bots/new"
        element={
          <RequireSession>
            <BotWizard />
          </RequireSession>
        }
      />
      <Route
        path="/bots/:botId"
        element={
          <RequireSession>
            <BotLayout />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="console" replace />} />
        <Route path="console" element={<OwnerConsoleRoute />} />
        <Route path="character" element={<CharacterEditor />} />
        <Route path="accounts" element={<ConnectedAccounts />} />
        <Route path="schedule" element={<ScheduleEditor />} />
        <Route path="library" element={<AssetLibrary />} />
        <Route path="listing" element={<ListingEditor />} />
        <Route path="dm" element={<DirectMessages />} />
        <Route path="activity" element={<ActivityLog />} />
      </Route>

      <Route
        path="/rentals"
        element={
          <RequireSession>
            <MyRentals />
          </RequireSession>
        }
      />
      <Route
        path="/rentals/:rentalId/approval"
        element={
          <RequireSession>
            <RentalApproval />
          </RequireSession>
        }
      />
      <Route
        path="/rentals/:rentalId"
        element={
          <RequireSession>
            <RentalLayout />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="console" replace />} />
        <Route path="console" element={<RenterConsoleRoute />} />
        <Route path="library" element={<AssetLibrary />} />
        {/* Same component under both branches; §7.4 decides what it renders. */}
        <Route path="dm" element={<DirectMessages />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="consent" element={<RentalConsent />} />
        <Route path="review" element={<RentalReview />} />
      </Route>

      <Route
        path="/account"
        element={
          <RequireSession>
            <AccountLayout />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="profile" replace />} />
        <Route path="billing" element={<Billing />} />
        <Route path="credits" element={<Credits />} />
        <Route path="profile" element={<Profile />} />
        <Route path="security" element={<Security />} />
        <Route path="notifications" element={<NotificationPreferencesRoute />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function App() {
  return (
    <SessionProvider>
      <NotificationsProvider>
        <BrowserRouter>
          <AppShell>
            <AppRoutes />
          </AppShell>
        </BrowserRouter>
      </NotificationsProvider>
    </SessionProvider>
  );
}
